/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/Home/ExpertHome.tsx.
 */

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import {
  isExpertActiveQueueStatus,
  normalizeReviewStatus,
  REVIEW_STATUS,
} from "../../../../constants/reviewStatus";
import StatCard from "../../../../components/common/StatCard";
import Button from "../../../../components/common/Button";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import "./ExpertHome.css";

interface Material {
  id: number;
  name: string;
  code: string;
}

interface Submission {
  id: string;
  materialId: number;
  reviewStatus?: string;
  dataReview?: string | null;
}

interface ExpertStats {
  assignedMaterials: number;
  pendingReviews: number;
  totalSubmissions: number;
  reviewedSubmissions: number;
}

interface Props {
  onNavigateToMaterials?: () => void;
  onNavigateToReviews?: () => void;
}

const ExpertHome: React.FC<Props> = ({ onNavigateToMaterials, onNavigateToReviews }) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [stats, setStats] = useState<ExpertStats>({
    assignedMaterials: 0,
    pendingReviews: 0,
    totalSubmissions: 0,
    reviewedSubmissions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch assigned materials
        const materialsRes = await axiosClient.get("/expert/materials");
        const materialsData = materialsRes.data?.success
          ? materialsRes.data.data || []
          : [];

        setMaterials(materialsData);

        const queueRes = await axiosClient.get(API_URLS.EXPERT.SUBMISSIONS).catch(() => ({
          data: { success: false, data: [] },
        }));
        const queueRows: any[] = queueRes.data?.success
          ? queueRes.data.data || []
          : Array.isArray(queueRes.data?.data)
            ? queueRes.data.data
            : [];

        const allSubmissions: Submission[] = queueRows.map((s: any) => ({
          id: s.submissionId || s.id,
          materialId: s.background?.materialId ?? s.materialId ?? 0,
          reviewStatus: s.reviewStatus ?? null,
          dataReview: s.dataReview ?? null,
        }));

        const pendingReviews = allSubmissions.filter((s) =>
          isExpertActiveQueueStatus(s.reviewStatus)
        ).length;

        const reviewedSubmissions = allSubmissions.filter((s) => {
          const r = normalizeReviewStatus(s.reviewStatus);
          return r === REVIEW_STATUS.APPROVED || r === REVIEW_STATUS.FLAGGED_BY_EXPERT;
        }).length;

        setStats({
          assignedMaterials: materialsData.length,
          pendingReviews,
          totalSubmissions: allSubmissions.length,
          reviewedSubmissions,
        });
      } catch (err: any) {
        console.error("Failed to fetch expert dashboard data:", err);
        setError(err.response?.data?.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="Expert Dashboard" />
        <div className="loading-placeholder">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="background-form-container analysis-shell">
        <AnalysisBanner title="Expert Dashboard" />
        <div className="error-placeholder">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="background-form-container analysis-shell">
      <AnalysisBanner title="Expert Dashboard" />
      <div className="expert-home-container">

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          value={stats.assignedMaterials}
          label="Assigned Materials"
        />
        <StatCard
          value={stats.pendingReviews}
          label="Pending Reviews"
          highlight={stats.pendingReviews > 0}
        />
        <StatCard
          value={stats.totalSubmissions}
          label="Total Submissions"
        />
        <StatCard
          value={stats.reviewedSubmissions}
          label="Reviewed Submissions"
        />
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <div className="section-header">
          <h2 className="section-title">Quick Actions</h2>
          <p className="section-subtitle">Get started with your review tasks</p>
        </div>
        <div className="actions-grid">
          <div className="action-card">
            <h3 className="action-title">View Assigned Materials</h3>
            <p className="action-description">
              Browse all materials assigned to you and access their submissions
            </p>
            <Button
              label="Go to Materials"
              onClick={onNavigateToMaterials}
              disabled={!onNavigateToMaterials}
              variant="default"
            />
          </div>
          <div className="action-card">
            <h3 className="action-title">Review Submissions</h3>
            <p className="action-description">
              {stats.pendingReviews > 0
                ? `You have ${stats.pendingReviews} submission${stats.pendingReviews > 1 ? "s" : ""} waiting for review`
                : "Review and approve submissions for your assigned materials"}
            </p>
            <Button
              label="Start Reviewing"
              onClick={onNavigateToReviews}
              disabled={!onNavigateToReviews}
              variant="default"
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

/** React component — Expert Home. */
export default ExpertHome;

